import { faArrowRight } from '@fortawesome/pro-light-svg-icons';
import { faCircleChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalculator, faTrophy } from '@fortawesome/sharp-regular-svg-icons';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import TrackerChallengeBox from '../../challenges/components/TrackerChallengeBox.js';
import { Button } from '../../shared/components/button/Button.tsx';
import { useHttpClient } from '../../shared/hooks/http-hook';
import { shouldShowChallengeOnTracker } from '../../shared/util/challenges.js';
import HorizontalSlider from '../components/HorizontalSlider';
import Influencers from '../components/Influencers';
import Journal from '../components/Journal';
import MindfulMinutes from '../components/MindfulMinutes';
import TrackerHeading from '../components/TrackerHeading';

function StressNMoodTracker(props) {
  const { t } = useTranslation();
  const pageid = window.location.pathname;
  const healthArea = pageid.split('/')[1];
  const challenge = useSelector((state) => state?.challenges?.challenge);
  const { sendRequest } = useHttpClient();
  const [isSaved, setIsSaved] = useState(false);
  const [date, setDate] = useState(false);
  const [data, setData] = useState(null);
  const [journal, setJournal] = useState(null);
  const [currentTracker, setCurrentTracker] = useState(null);
  const [moodValue, setMoodValue] = useState(null);
  const [offPlatform, setOffPlatform] = useState(null);
  const [inPlatform, setInPlatform] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const moodValues = [
    {
      id: 1,
      title: `${t('trackers.sliders.mood.negative')} (1)`,
      label: `${t('trackers.sliders.mood.negative')} (1)`,
    },
    {
      id: 2,
      title: `${t('trackers.sliders.mood.low')} (2)`,
      label: `${t('trackers.sliders.mood.low')} (2)`,
    },
    {
      id: 3,
      title: `${t('trackers.sliders.mood.neutral')} (3)`,
      label: `${t('trackers.sliders.mood.neutral')} (3)`,
    },
    {
      id: 4,
      title: `${t('trackers.sliders.mood.good')} (4)`,
      label: `${t('trackers.sliders.mood.good')} (4)`,
    },
    {
      id: 5,
      title: `${t('trackers.sliders.mood.positive')} (5)`,
      label: `${t('trackers.sliders.mood.positive')} (5)`,
    },
  ];

  useEffect(() => {
    if (data && data.mood_rating) {
      const newMoodValue = Number(data.mood_rating) * 20;
      setMoodValue(newMoodValue);
    }
  }, [data]);

  const getData = async (date) => {
    if (date.length > 0) {
      try {
        const response = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/trackers/mood/${date}`
        );
        if (response && response.tracker?._id) {
          const { tracker } = response;
          setCurrentTracker({
            id: tracker._id,
            trackerDate: tracker.tracker_date.toString().slice(0, 10),
          });
          const data = JSON.parse(tracker.data);
          setData(data);
          setMoodValue(Number(data.mood_rating) * 20);
          setOffPlatform(data.mindful_minutes[0].off_platform);
          setInPlatform(data.mindful_minutes[1].in_platform);
          setJournal({
            enhancers: tracker.enhancers,
            inhibitors: tracker.inhibitors,
            journal: tracker.journal,
          });
          setIsSaved(true);
        } else {
          setCurrentTracker({
            id: '',
            trackerDate: date,
          });
          setData({
            mood_rating: '3',
            mindful_minutes: [{ off_platform: 0 }, { in_platform: 0 }],
          });
          setMoodValue(50);
          setOffPlatform(0);
          setInPlatform(0);
          setJournal(() => ({
            enhancers: [],
            inhibitors: [],
            journal: '',
          }));
        }
      } catch (err) {
        console.log(err);
      }
    }
  };

  useEffect(() => {
    getData(date);
  }, [date]);

  const saveResponse = (location, stat) => {
    if (location === 'journal') {
      setJournal((prevState) => {
        return {
          ...prevState,
          [stat.id]: stat.value,
        };
      });
    } else {
      setData((prevState) => {
        return {
          ...prevState,
          [stat.id]: stat.value,
        };
      });
    }
  };

  const sendData = async () => {
    let method = 'POST';
    let url = `${process.env.REACT_APP_BACKEND_URL}/trackers/`;
    if (currentTracker.id !== '') {
      method = 'PUT';
      url = `${process.env.REACT_APP_BACKEND_URL}/trackers/${currentTracker.id}`;
    }

    try {
      const response = await sendRequest(
        url,
        method,
        JSON.stringify({
          trackerDate: date,
          type: 'mood',
          data,
          hidden: false,
          enhancers: journal.enhancers,
          inhibitors: journal.inhibitors,
          journal: journal.journal,
          platform: 'web',
        })
      );
      if (response) {
        setIsSaved(true);
        props.closeTrackers();
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleMinutes = (value) => {
    setOffPlatform(value);
  };

  const saveMood = () => {
    if (isUpdating) return;

    setIsUpdating(true);
    setData((prevState) => {
      return {
        ...prevState,
        mood_rating: Math.ceil(moodValue / 20),
      };
    });
    setIsUpdating(false);
  };

  const saveMinutes = () => {
    setData((prevState) => {
      return {
        ...prevState,
        mindful_minutes: [{ off_platform: offPlatform || 0 }, { in_platform: inPlatform || 0 }],
      };
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    saveMood();
    saveMinutes();
    sendData();
  };

  return (
    <div
      id="snm-mood"
      className="tracker-container"
      role="region"
      aria-label={`${t('T348')} ${t('tracker.region.title')}`}
    >
      <form
        id="mood-tracker-form"
        className="tracker-form"
        onSubmit={handleSubmit}
        noValidate={false}
      >
        <div className="trackers-inner">
          <div className="back-to-main">
            <button
              className="back-btn"
              aria-label={t('A136')}
              title={t('A136')}
              onClick={props.handleBackToMain}
              type="button"
            >
              <FontAwesomeIcon icon={faCircleChevronLeft} />
            </button>
          </div>
          <TrackerHeading title="T201" setDate={setDate} isSaved={isSaved} />
          <div className="trackers-body" role="group" aria-label={t('modal.tracker.details')}>
            <div className="ind-tracker">
              {shouldShowChallengeOnTracker(healthArea, challenge?.type, challenge?.isActive) && (
                <TrackerChallengeBox />
              )}
              <div role="group" aria-labelledby="mood-tracker-slider">
                <h2 className="title" id="mood-tracker-slider">
                  {t('T202')}
                </h2>
                <span className="sr-only">{t('trackers.mood.info"')}</span>
                <HorizontalSlider
                  questionid="mood_rating"
                  data={data}
                  saveResponse={saveResponse}
                  aria-labelledby="mood-tracker-slider"
                  customValues={moodValues}
                  labels={['T203', 'T204']}
                  isReversed
                  required
                  title={t('T202')}
                />
              </div>
              <input
                type="hidden"
                name="mood_rating"
                value={data?.mood_rating || ''}
                required
                aria-label={t('T202')}
              />
            </div>
            <div className="ind-tracker" role="group" aria-labelledby="mood-tracker-influencers">
              <div className="tracker-content-heading">
                <h2 className="title" id="mood-tracker-influencers">
                  {t('T205')}
                </h2>
                <p className="optional-text">{t('T019')}</p>
              </div>
              <Influencers
                mood
                twoColumns
                moodValue={moodValue}
                questionid={['enhancers', 'inhibitors']}
                data={journal}
                saveResponse={saveResponse}
                greenListAriaLabel={t('trackers.influencers.mood')}
                titleId="mood-tracker-influencers"
              />
            </div>
            <fieldset
              className="ind-tracker no-padding"
              onMouseLeave={saveMinutes}
              onTouchEnd={saveMinutes}
            >
              <legend className="title title-with-border" id="mood-tracker-mindful-minutes-title">
                {t('T206')}
                {shouldShowChallengeOnTracker(healthArea, challenge?.type, challenge?.isActive) && (
                  <div className="tc-badge">
                    <FontAwesomeIcon icon={faTrophy} /> <p>{t('T017')}</p>
                  </div>
                )}
              </legend>
              <div className="buffer" />
              <div className="whc-cont fs-exclude">
                <div className="whc-body">
                  <div className="whc-heading">
                    <FontAwesomeIcon icon={faCalculator} /> {t('T209')}
                  </div>
                  <div className="whc-text">{t('T210')}</div>
                </div>
              </div>

              <MindfulMinutes
                offPlatform={offPlatform}
                inPlatform={inPlatform}
                handleMinutes={handleMinutes}
              />

              <div className="buffer" />
            </fieldset>
            <div className="ind-tracker" role="group" aria-label={`${t('T348')} - ${t('T018')}`}>
              <Journal
                placeholder={t('T020')}
                questionid="journal"
                data={journal}
                saveResponse={saveResponse}
              />
            </div>
          </div>
        </div>
        <div className="buttons">
          <Button onClick={props.closeTrackers} variant="secondary" color="dark" type="button">
            {t('T015')}
          </Button>
          <Button
            type="submit"
            form="mood-tracker-form"
            icon={{ faIconDefinition: faArrowRight }}
            iconPosition="right"
          >
            {t('T016')}
          </Button>
        </div>
      </form>
    </div>
  );
}
export default StressNMoodTracker;
